import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ReportDialog } from './ReportDialog';
import type { Merchant } from '@/lib/merchants';
import { getMerchantStatus, formatRelativeTime, getCategoryInfo } from '@/lib/merchants';
import { sanitizeUrl } from '@/lib/sanitize';
import { VerifiedBadge } from './VerifiedBadge';
import { SponsoredBadge } from './SponsoredBadge';
import { StatusBadge } from './StatusBadge';
import { LocationLabel } from './LocationLabel';
import { MapPin, ExternalLink, Clock, Globe, Hash, User, Navigation, Share2, Flag, ShieldCheck, BadgeCheck, Store } from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { toast } from '@/hooks/useToast';

interface MerchantDetailProps {
  merchant: Merchant;
}

/** Build a directions URL that works on both mobile (Apple Maps / Google Maps app) and desktop. */
function getDirectionsUrl(lat: number, lng: number, name: string): string {
  const query = encodeURIComponent(name);
  // Google Maps universal link — opens in-app on mobile, browser on desktop
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&destination_place_id=&travelmode=driving&query=${query}`;
}

export function MerchantDetail({ merchant }: MerchantDetailProps) {
  const status = getMerchantStatus(merchant);
  const npub = nip19.npubEncode(merchant.pubkey);
  const [reportOpen, setReportOpen] = useState(false);

  const merchantUrl = `${window.location.origin}/merchant/${npub}`;

  const handleShare = async () => {
    // Use the Web Share API if available (mobile), otherwise copy to clipboard
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${merchant.name} — BCH Local`,
          text: `Check out ${merchant.name} on BCH Local`,
          url: merchantUrl,
        });
      } catch (err) {
        // User cancelled share — not an error
        if (err instanceof Error && err.name !== 'AbortError') {
          copyToClipboard();
        }
      }
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(merchantUrl);
      toast({
        title: 'Link copied',
        description: 'Merchant link copied to clipboard',
      });
    } catch {
      toast({
        title: 'Copy failed',
        description: 'Could not copy link. Try selecting the URL manually.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2 min-w-0 flex-wrap">
            <h2 className="text-xl font-bold leading-tight truncate">{merchant.name}</h2>
            {merchant.verified && <VerifiedBadge size="md" label />}
            {merchant.sponsored && <SponsoredBadge size="md" />}
          </div>
          <StatusBadge status={status} size="md" className="shrink-0" />
        </div>

        {/* Verified callout or nudge */}
        {merchant.verified ? (
          <div className="flex items-center gap-2.5 rounded-lg bg-bch px-3 py-2.5 mb-1 shadow-sm">
            <BadgeCheck className="size-5 text-bch-foreground shrink-0" strokeWidth={2.5} />
            <div className="min-w-0">
              <div className="text-sm font-bold text-bch-foreground leading-tight">Verified BCH Merchant</div>
              <div className="text-[11px] text-bch-foreground/80 leading-tight">Confirmed to accept Bitcoin Cash</div>
            </div>
          </div>
        ) : (
          <Link
            to="/get-verified"
            className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 mb-1 hover:bg-muted transition-colors group"
          >
            <ShieldCheck className="size-4 text-muted-foreground shrink-0 group-hover:text-bch transition-colors" />
            <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors">
              This merchant is not verified. <span className="underline">Learn more</span>
            </span>
          </Link>
        )}

        {merchant.summary && (
          <p className="text-sm text-muted-foreground leading-relaxed">
            {merchant.summary}
          </p>
        )}
      </div>

      <Separator />

      {/* Categories */}
      <div>
        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
          Categories
        </h4>
        <div className="flex flex-wrap gap-1.5">
          {merchant.categories.length > 0 ? (
            merchant.categories.map(cat => {
              const info = getCategoryInfo(cat);
              return (
                <Badge key={cat} variant="secondary" className="text-sm py-0.5 px-2.5">
                  {info.emoji} {info.label}
                </Badge>
              );
            })
          ) : (
            <Badge variant="secondary" className="text-sm py-0.5 px-2.5">
              Merchant
            </Badge>
          )}
        </div>
      </div>

      {/* Details list */}
      <div className="space-y-3">
        {merchant.website && (
          <div className="flex items-center gap-3">
            <Globe className="size-4 text-muted-foreground shrink-0" />
            <a
              href={sanitizeUrl(merchant.website)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary hover:underline truncate"
            >
              {merchant.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
            </a>
          </div>
        )}

        {merchant.location ? (
          <div className="flex items-center gap-3">
            <MapPin className="size-4 text-muted-foreground shrink-0" />
            <span className="text-sm">
              <LocationLabel lat={merchant.location.lat} lng={merchant.location.lng} fallback={merchant.geohash} />
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <MapPin className="size-4 text-muted-foreground shrink-0" />
            <span className="text-sm text-muted-foreground">
              No map location — online or home-based business
            </span>
          </div>
        )}

        {merchant.lastActive && (
          <div className="flex items-center gap-3">
            <Clock className="size-4 text-muted-foreground shrink-0" />
            <span className="text-sm">
              Last active: <span className="font-medium">{formatRelativeTime(merchant.lastActive)}</span>
            </span>
          </div>
        )}

        <div className="flex items-center gap-3">
          <User className="size-4 text-muted-foreground shrink-0" />
          <span className="text-xs font-mono text-muted-foreground truncate">
            {npub.slice(0, 16)}...{npub.slice(-8)}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <Hash className="size-4 text-muted-foreground shrink-0" />
          <span className="text-xs font-mono text-muted-foreground">
            Kind {merchant.event.kind}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <Store className="size-4 text-muted-foreground shrink-0" />
          <span className="text-sm">
            Listed via{' '}
            {merchant.source === 'bchnostr' ? (
              <a
                href={`https://bchnostr.com/profile/${npub}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-primary hover:underline"
              >
                BCHNostr
              </a>
            ) : (
              <a
                href="https://bchpay.cash"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-primary hover:underline"
              >
                BCHPAY.Cash
              </a>
            )}
          </span>
        </div>
      </div>

      <Separator />

      {/* Action buttons */}
      <div className="space-y-2">
        {/* Visit Website */}
        {merchant.website && (
          <Button asChild className="w-full bg-bch hover:bg-bch/90 text-bch-foreground">
            <a
              href={sanitizeUrl(merchant.website)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink className="size-4 mr-2" />
              Visit Website
            </a>
          </Button>
        )}

        {/* Get Directions */}
        {merchant.location && (
          <Button asChild variant="outline" className="w-full">
            <a
              href={getDirectionsUrl(merchant.location.lat, merchant.location.lng, merchant.name)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Navigation className="size-4 mr-2" />
              Get Directions
            </a>
          </Button>
        )}

        {/* Share + Report row */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={handleShare}
          >
            <Share2 className="size-4 mr-2" />
            Share
          </Button>
          <Button
            variant="ghost"
            className="text-muted-foreground hover:text-destructive"
            onClick={() => setReportOpen(true)}
          >
            <Flag className="size-4 mr-2" />
            Report
          </Button>
        </div>
      </div>

      {/* Report dialog */}
      <ReportDialog
        merchant={merchant}
        open={reportOpen}
        onOpenChange={setReportOpen}
      />
    </div>
  );
}
